import {ComponentType, Suspense, useEffect, useRef} from "react";
import {Navigate, Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {objPathInit, objRmKeysC} from "./CommonJS";
import {LoadingAnim} from "./CommonHtml";
import {setRumPage} from "../app/rum";

/** Shape returned by useLevelNavigation */
export interface LevelNavigation {
    to(
        path: string,
        params?: Record<string, any>,
        opts?: {isReplace?: boolean; isReset?: boolean; hash?: string}
    ): void;
    navUp(toDepth: number): void;
    reload(): void;
    getCurPath(): string;
    getPathRoot(): string;
    getMenuPath(): string;
    getParams(): Record<string, any>;
    updateParams(params: Record<string, any>): Record<string, any>;
    getTitle(idx?: number): string | undefined;
    getDefs(idx?: number): Record<string, any>;
    /** true while handling the auth-code callback */
    isInLogin: boolean;
    getPaths(): string[];
    getParamsRoot(): Record<string, any>;
    getDepth(): number;
    sSubPath(sub: string): string;
}


export function useLevelNavigation(routes, defaultPath) {
    const vals = useRef({db: reset(), paramUpdates: {}, redirTo: "", prevLoc: ""}).current;
    const location = useLocation();
    const navigate = useNavigate();
    let isInLogin = false;

    useEffect(() => {
        if (!vals.redirTo) return;
        const to = vals.redirTo;
        vals.redirTo = "";
        navigate(to, {replace: true});
    }, [location]);

    useEffect(() => {
        let timeout;
        if (location.hash)
            timeout = setTimeout(() => {
                const element = document.getElementById(location.hash.slice(1));
                if (element)
                    element.scrollIntoView({behavior: "smooth", block: "start"});
            }, 700);

        else
            window.scrollTo(0, 0);


        return () => {
            clearTimeout(timeout);
        };

    }, [location.pathname, location.hash]);

    if (!routes[location.pathname])
        return histReplace(defaultPath);
    if (vals.prevLoc !== location.pathname + location.search + location.hash) {
        vals.prevLoc = location.pathname + location.search + location.hash;
        let newDb;
        try {
            if (location.search.slice(0, 5) === "?code")
                isInLogin = true;
            else
                newDb = location.search && JSON.parse(decodeURIComponent(escape(atob(location.search.slice(1)))));
            newDb?.params?.forEach(p => initParams(p));      // ensure old links
        } catch (err) {
            console.log("Invalid page location:", location.search.slice(1), err.toString());
            return histReplace(defaultPath);
        }

        if (newDb)
            vals.db = newDb;
        else if (routes[location.pathname]?.redirect)
            return histReplace(routes[location.pathname].redirect);
        else if (routes[location.pathname])
            vals.db = {depth: 0, params: [], paths: [location.pathname]};
        else
            return histReplace(defaultPath);
        setRumPage(`${location.pathname}: ${getTitle() ?? ""}`, getParams());
        !location.hash && window.scrollTo(0, 0);
    }

    return {
        to, navUp, reload, getCurPath, getPathRoot, getMenuPath, getParams, updateParams, getTitle, getDefs, isInLogin,
        getPaths: () => vals.db.paths,
        getParamsRoot: () => vals.db.params[0] || (vals.db.params[0] = {}),
        getDepth: () => vals.db.depth,
        sSubPath: sub => `/${getPathRoot()}/${sub}`,
    };

    function reload() {return doPush(getCurPath(), "");}
    function getCurPath() {return vals.db.paths[vals.db.depth];}
    function getPathRoot() {return getCurPath().split("/")[1];}
    function getMenuPath() {return routes[vals.db.paths[0]]?.redirect || vals.db.paths[0];}    // Sidebar/menu anchor: chain root, falling back to its redirect target.
    function getTitle(idx = vals.db.depth) {
        const path = vals.db.paths[idx];
        const rt = routes[path], {name, tName} = rt;
        const {tObj} = vals.db.params?.[idx] ?? {};
        return !tName || !tObj ? name : tName === "$1" ? tObj[0] : tName.format(...tObj);
    }
    function getDefs(idx = vals.db.depth) {
        const path = vals.db.paths[idx];
        return routes[path].defs || {};
    }

    function to(path, params, {isReplace = false, isReset = false, hash = ""} = {}) {
        if (isReset)
            vals.db = reset();
        else if (!isReplace)
            vals.db.depth++;

        vals.db.paths[vals.db.depth] = path;
        vals.db.params[vals.db.depth] = initParams(params);

        doPush(path, hash);
    }

    function initParams(params: {_lnParams?: object} = {}) {
        const lp = params._lnParams || Date.now();
        return Object.assign(params, {_lnParams: lp});
    }

    function _getParams() {
        const oParams = vals.db.params[vals.db.depth] || (vals.db.params[vals.db.depth] = initParams());
        return Object.assign(oParams, vals.paramUpdates[oParams._lnParams] ?? {});
    }
    function getParams() {
        return objRmKeysC(_getParams(), "_lnParams");
    }

    // provide an object with additional params that will be added to the original page params.
    function updateParams(params) {
        if (params && typeof params === "object")
            Object.assign(objPathInit(vals.paramUpdates, _getParams()._lnParams), params);
        return getParams();
    }

    function navUp(toDepth) {
        vals.db.depth = toDepth;
        vals.db.paths = vals.db.paths.slice(0, toDepth + 1);
        vals.db.params = vals.db.params.slice(0, toDepth + 1);
        doPush(vals.db.paths[toDepth]);
    }

    function reset() {return {depth: 0, params: [], paths: []};}

    function histReplace(newPath) {
        console.log("Redirecting to", newPath);
        vals.redirTo = newPath + location.hash;
        return {redirected: true};
    }

    function doPush(path, hash = "") {
        routes[path].instance = (routes[path].instance ?? 0) + 1;

        // unescape URI: leverage UTF-8 processing in URIComponent handling
        navigate({
            pathname: path,
            search: btoa(unescape(encodeURIComponent(JSON.stringify(vals.db)))),
            hash: hash
        });
    }
}


export interface AppRoute {
    Comp: ComponentType<any>;                    // React component rendered for the route
    exact?: boolean;                             // If true, path must match exactly
    name?: string;                               // User-facing page name (e.g. for menus)
    tName?: string;                              // Optional title pattern (e.g. "$1" → tObj[0])
    defs?: Record<string, unknown>;              // Route-specific definitions (used in getDefs)
    redirect?: string;                           // If set, auto-redirects to this path
    instance?: number;                           // Internal counter used to force remounts
}

export type RouteMap = Record<string, AppRoute>;

export const LazyRoutes = ({routes, defaultPath}: {routes: RouteMap, defaultPath: string}) =>
    <Suspense fallback={<LoadingAnim classNameSize="w-5 h-5"/>}>
        <Routes>
            {Object.entries(routes).map(([path, {Comp, exact, name, instance}]) =>
                <Route key={path + (exact ? "" : "/*")} {...{path, name}} element={<Comp key={path + (instance ?? 0)}/>}/>)}
            <Route path="/" element={<Navigate replace to={defaultPath}/>}/>
        </Routes>
    </Suspense>;
